<!--
  ImageUploader.vue -- reusable click-or-drag image dropzone.

  Phase 1 PR-4 of the email-template customization feature. Shared,
  generic uploader used first by the Email Notifications header-image
  card (this PR) and imported UNCHANGED by the Branding brand-image card
  (PR-5) -- hence it lives in the shared components dir and is NOT
  email-specific.

  Contract (fully controlled by the parent):

    props:
      currentImage : String|null  -- the current image URL to preview
                                     (parent resolves key -> URL); null =
                                     empty state (dropzone).
      title        : String       -- empty-state heading ("Add a header
                                     image").
      hint         : String       -- empty-state helper copy.
      disabled     : Boolean      -- read-only (store lifecycle lock).
      uploading    : Boolean      -- parent-driven "upload in flight"
                                     spinner/label.
      testidPrefix : String       -- data-testid namespace so two
                                     uploaders on one page stay distinct.

    emits:
      upload(File)  -- user picked/dropped a valid file; the PARENT owns
                       the actual POST + persistence (it knows which
                       column: email_image here, brand_image in PR-5).
      remove()      -- user cleared the image.
      error(String) -- client-side validation failure (bad type/size),
                       so the parent can toast it.

  The component NEVER calls the API itself -- persistence is the parent's
  job -- so it stays reusable across the two different persistence paths.
  File validation reuses the shared artworkUploadContract so every upload
  surface enforces one contract.
-->
<template>
  <div class="image-uploader" :data-testid="rootTestid">
    <!-- Empty state: click-or-drag dropzone -->
    <div
      v-if="!currentImage"
      class="image-uploader__dropzone"
      :class="{
        'image-uploader__dropzone--dragover': dragOver,
        'image-uploader__dropzone--disabled': disabled,
      }"
      :data-testid="`${testidPrefix}-dropzone`"
      role="button"
      tabindex="0"
      :aria-label="title"
      :aria-disabled="disabled ? 'true' : 'false'"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <v-icon class="image-uploader__icon" aria-hidden="true"
        >add_photo_alternate</v-icon
      >
      <div class="image-uploader__title">{{ title }}</div>
      <div class="image-uploader__hint">
        {{ hint }}
        <template v-if="hint"><br /></template>
        <span class="image-uploader__hint-cta">Click to upload</span> or drag a
        file here.
      </div>
      <div
        v-if="uploading"
        class="image-uploader__status"
        :data-testid="`${testidPrefix}-uploading`"
      >
        Uploading&hellip;
      </div>
    </div>

    <!-- Filled state: preview + Replace / Remove -->
    <div v-else :data-testid="`${testidPrefix}-preview`">
      <div class="image-uploader__banner">
        <img
          class="image-uploader__img"
          :src="currentImage"
          alt="Uploaded image preview"
          :data-testid="`${testidPrefix}-image`"
        />
        <div class="image-uploader__actions">
          <button
            type="button"
            class="image-uploader__btn"
            :disabled="disabled"
            :data-testid="`${testidPrefix}-replace`"
            @click="openPicker"
          >
            {{ isDefault ? 'Upload image' : 'Replace' }}
          </button>
          <!-- Remove only makes sense for an OWNED image. When the preview is
               an inherited default (isDefault) there is nothing to remove (the
               override is already empty), so we hide it to avoid the
               "Remove does nothing" confusion. -->
          <button
            v-if="!isDefault"
            type="button"
            class="image-uploader__btn image-uploader__btn--danger"
            :disabled="disabled"
            :data-testid="`${testidPrefix}-remove`"
            @click="onRemove"
          >
            Remove
          </button>
        </div>
        <div
          v-if="uploading"
          class="image-uploader__status image-uploader__status--overlay"
          :data-testid="`${testidPrefix}-uploading`"
        >
          Uploading&hellip;
        </div>
      </div>
      <!-- BELOW the banner, not inside it (fixed 2026-09-23).

           `.image-uploader__banner` is `display: flex` with the default row
           direction, so a caption nested inside it became a second COLUMN
           beside the image: the text wrapped into a narrow ragged strip down
           the right-hand side and painted over the artwork. Jake read it off
           the email header image as overlapping the AdventHealth logo.

           Its `margin-top: 6px` was always written for a block layout
           sitting under the preview -- which is what the prop's own doc
           comment says it is ("shown under the preview"). Moving it out of
           the flex row is what makes that true, and it keeps the caption
           inside the filled-state wrapper so it still only renders when
           there IS an image. -->
      <div
        v-if="isDefault"
        class="image-uploader__default-caption"
        :data-testid="`${testidPrefix}-default-caption`"
      >
        {{ defaultLabel }}
      </div>
    </div>

    <!-- Hidden native file input -->
    <input
      ref="fileInput"
      type="file"
      class="image-uploader__file"
      :accept="accept"
      :data-testid="`${testidPrefix}-input`"
      @change="onFileChange"
    />
  </div>
</template>

<script>
import {
  ARTWORK_UPLOAD_ACCEPT,
  validateArtworkFile,
} from "@/utils/artworkUploadContract";

export default {
  name: "ImageUploader",

  props: {
    currentImage: { type: String, default: null },
    title: { type: String, default: "Add an image" },
    hint: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    uploading: { type: Boolean, default: false },
    // Namespace so multiple uploaders on one page keep distinct testids.
    testidPrefix: { type: String, default: "image-uploader" },
    // When true, ``currentImage`` is an INHERITED default (e.g. the email
    // header falling back to the store brand image), NOT an owned override.
    // In that state there is nothing to "Remove" -- the override is already
    // empty -- so we show a "using default" caption and drop the Remove
    // button, keeping only the upload action to SET an override. Default
    // false so an owned image (the Branding brand-image mount, or a set
    // email override) keeps the normal Replace/Remove controls.
    isDefault: { type: Boolean, default: false },
    // Optional caption shown under the preview when isDefault is true.
    defaultLabel: { type: String, default: "Using your brand image" },
  },

  emits: ["upload", "remove", "error"],

  data() {
    return {
      dragOver: false,
      accept: ARTWORK_UPLOAD_ACCEPT,
    };
  },

  computed: {
    rootTestid() {
      return `${this.testidPrefix}-root`;
    },
  },

  methods: {
    openPicker() {
      if (this.disabled) return;
      const input = this.$refs.fileInput;
      if (input) {
        input.value = "";
        input.click();
      }
    },

    onDragOver() {
      if (this.disabled) return;
      this.dragOver = true;
    },

    onDragLeave() {
      this.dragOver = false;
    },

    onDrop(event) {
      this.dragOver = false;
      if (this.disabled) return;
      const file = event?.dataTransfer?.files?.[0];
      this.handleFile(file);
    },

    onFileChange(event) {
      const file = event?.target?.files?.[0];
      this.handleFile(file);
    },

    // Validate against the shared upload contract, then hand the raw File
    // to the parent -- the parent owns the POST + persistence.
    handleFile(file) {
      if (!file) return;
      const { ok, error } = validateArtworkFile(file);
      if (!ok) {
        this.$emit("error", error);
        return;
      }
      this.$emit("upload", file);
    },

    onRemove() {
      if (this.disabled) return;
      this.$emit("remove");
    },
  },
};
</script>

<style scoped>
.image-uploader__file {
  display: none;
}
.image-uploader__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--tf-sp-2, 8px);
  padding: var(--tf-sp-6, 24px);
  border: 1.5px dashed var(--tf-border, #cfd8e3);
  border-radius: var(--tf-radius-md, 12px);
  background: var(--tf-bg-subtle, #f8fafc);
  text-align: center;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
}
.image-uploader__dropzone:hover,
.image-uploader__dropzone:focus-visible {
  border-color: var(--tf-primary, #119ae9);
  outline: none;
}
.image-uploader__dropzone--dragover {
  border-color: var(--tf-primary, #119ae9);
  background: var(--tf-primary-soft, #e3f2fd);
}
.image-uploader__dropzone--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.image-uploader__icon {
  font-size: 28px;
  color: var(--tf-primary, #119ae9);
}
.image-uploader__title {
  font-size: 14px;
  font-weight: 500;
  color: var(--tf-fg-2, #37474f);
}
.image-uploader__hint {
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--tf-fg-4, #78909c);
  max-width: 340px;
}
.image-uploader__hint-cta {
  color: var(--tf-primary, #119ae9);
  font-weight: 500;
}
.image-uploader__banner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 96px;
  padding: var(--tf-sp-2, 8px);
  border: 1px solid var(--tf-border, #cfd8e3);
  border-radius: var(--tf-radius-md, 12px);
  background: var(--tf-bg-subtle, #f8fafc);
  overflow: hidden;
}
.image-uploader__img {
  max-height: 120px;
  max-width: 100%;
  object-fit: contain;
}
.image-uploader__actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
}
.image-uploader__btn {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid var(--tf-border, #cfd8e3);
  border-radius: 9999px;
  padding: 4px 11px;
  font-size: 12px;
  color: var(--tf-fg-2, #37474f);
  cursor: pointer;
}
.image-uploader__btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.image-uploader__btn--danger {
  color: var(--tf-danger, #d32f2f);
  border-color: #f3c9c9;
}
.image-uploader__status {
  font-size: 12px;
  color: var(--tf-fg-4, #78909c);
}
.image-uploader__status--overlay {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 8px;
  border-radius: 6px;
}
.image-uploader__default-caption {
  margin-top: 6px;
  font-size: 12px;
  color: var(--tf-fg-4, #78909c);
  font-style: italic;
}
</style>
